<script setup lang="ts">
import { computed, ref } from 'vue'
import type { CSSProperties } from 'vue'

import placeholderSrc from '../../assets/placeholders/image.svg'
import { useI18n } from '../../i18n'

const props = defineProps<{
  src: string
  alt?: string
  style?: CSSProperties
}>()

const { t } = useI18n()

const hasLoadError = ref(false)

const requestedSrc = computed(() => (typeof props.src === 'string' ? props.src.trim() : ''))

const effectiveSrc = computed(() => {
  if (!requestedSrc.value) return placeholderSrc
  return hasLoadError.value ? placeholderSrc : requestedSrc.value
})

const effectiveAlt = computed(() => (requestedSrc.value ? (props.alt ?? '') : ''))

const showErrorOverlay = computed(() => requestedSrc.value.length > 0 && hasLoadError.value)

const handleError = () => {
  hasLoadError.value = true
}
</script>

<template>
  <div class="image-shell" :style="props.style">
    <img
      class="image-media"
      :src="effectiveSrc"
      :alt="effectiveAlt"
      draggable="false"
      @error="handleError"
    />

    <div v-if="showErrorOverlay" class="image-error" aria-live="polite">
      {{ t('elements.image.loadError') }}
    </div>
  </div>
</template>

<style scoped>
.image-shell {
  position: absolute;
  display: block;
}

.image-media {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.image-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.4rem;
  font-size: 0.7rem;
  line-height: 1.2;
  font-weight: 650;
  color: #b91c1c;
  background: rgba(254, 242, 242, 0.88);
  border: 1px dashed rgba(248, 113, 113, 0.7);
  border-radius: 0.6rem;
  pointer-events: none;
}
</style>
